import React, { useState } from 'react';
import styled from 'styled-components';
import useAsyncEffect from 'use-async-effect';
import { useDispatch, useSelector } from 'react-redux';
import { IGlobalState } from '../model/IGlobalState';
import * as Roles from '../model/Roles';
import { Editor } from '@tinymce/tinymce-react';
import { loading } from '../actions/Loading';
import api from '../api/api';
import AnimatedSaveButton from './AnimatedSaveButton';
import AlertEx from './AlertEx';
import TextInput from './form/TextInput';
import FormTextItem from './form/FormTextItem';
import Tab from './Tab';

interface IProps {
    match?: { params: { combinedTicker: string; announcementGuid: string } };
}

const Style = styled.div`
.tab-content {
    margin-top: 1rem;
}
.new-campaign-actions{
    display:flex;
    margin: 1rem 0;
    align-items: center;
}
.new-campaign-actions > * {
    margin-right: 1rem;
}
.new-campaign-subject{
    max-width: 600px;
}
`;

const EditAnnouncementCampaignPage: React.FC<IProps> = (props: IProps) => {
    const dispatch = useDispatch();
    const user = useSelector((state: IGlobalState) => state.user);
    const combinedTicker = decodeURIComponent(props.match!.params.combinedTicker);
    const announcementGuid = decodeURIComponent(props.match!.params.announcementGuid);
    const [editorContent, setEditorContent] = useState<string>('');
    // the full campaign document — TinyMCE only edits the <body>, so the original
    // <head> (fonts, media queries, template styles) is re-attached for preview/send
    const [originalHtml, setOriginalHtml] = useState<string>('');
    const [subject, setSubject] = useState<string>('');
    const [testEmailAddresses, setTestEmailAddresses] = useState<string>('');
    const [isSending, setIsSending] = useState<boolean>(false);
    const [hasSent, setHasSent] = useState<boolean>(false);
    const [hasSentPreview, setHasSentPreview] = useState<boolean>(false);
    const [savedEditorContent, setSavedEditorContent] = useState<string>();
    const [savedSubject, setSavedSubject] = useState<string>();
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [loaded, setLoaded] = useState<boolean>(false);
    const [editorReady, setEditorReady] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<string>('Editor');

    useAsyncEffect(async (isMounted: () => boolean) => {
        dispatch(loading(true));
        const result = await api().getAnnouncementEditorHtml(combinedTicker, announcementGuid);
        if (!isMounted()) return;

        if (result.success) {
            setOriginalHtml(result.html);
            const body = result.html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
            setEditorContent(body ? body[1] : result.html);
            setSubject(result.subject);
            setTestEmailAddresses(result.testEmailAddresses || '');
        } else {
            setErrorMessage(result.errorMessage);
        }
        setLoaded(true);
        dispatch(loading(false));
    }, []);

    // edited body re-wrapped in the original document (head/styles intact)
    const fullHtml = (): string => {
        if (!/<body[^>]*>[\s\S]*<\/body>/i.test(originalHtml)) return editorContent;
        return originalHtml.replace(/(<body[^>]*>)[\s\S]*(<\/body>)/i, (_, open, close) => `${open}${editorContent}${close}`);
    };

    // the document's <style> blocks, injected into TinyMCE so the editor renders like the real email
    const templateCss = (originalHtml.match(/<style[^>]*>[\s\S]*?<\/style>/gi) || [])
        .map(s => s.replace(/<\/?style[^>]*>/gi, ''))
        .join('\n');

    const sendPreview = async () => {
        if (!testEmailAddresses.trim()) {
            AlertEx('Enter Test Email Addresses first');
            return false;
        }
        setIsSending(true);
        const result = await api().sendAnnouncementEditorPreview(combinedTicker, announcementGuid, fullHtml(), subject, testEmailAddresses);
        setIsSending(false);
        if (result.success) {
            setSavedEditorContent(editorContent);
            setSavedSubject(subject);
            setHasSentPreview(true);
            AlertEx('Preview sent');
            return true;
        }
        AlertEx(result.errorMessage);
        return false;
    };

    const sendCampaign = async () => {
        if (window.confirm(`Send edited announcement to subscribers?\r\n\r\nAre you sure?`)) {
            setIsSending(true);
            const result = await api().sendAnnouncementEditorCampaign(combinedTicker, announcementGuid, fullHtml(), subject);
            setIsSending(false);
            if (result.success) {
                setHasSent(true);
                AlertEx('Sent!');
                return true;
            }
            AlertEx(result.errorMessage);
        }
        return false;
    };

    const sendDisabled = (): boolean => {
        if (isSending || !hasSentPreview || hasSent || editorContent !== savedEditorContent || subject !== savedSubject) {
            return true;
        }
        return false;
    };

    return <Style>
        {!!errorMessage && <p className='error'>{errorMessage}</p>}

        {loaded && !errorMessage && <>
            <div className='new-campaign-actions'>
                <AnimatedSaveButton onClick={sendPreview} startingText="Send Preview" progressText="Sending..." completedText="Sent" disabled={isSending || hasSent} />
                <AnimatedSaveButton onClick={sendCampaign} startingText={hasSentPreview ? "Send Campaign" : "Send Campaign (must preview first)"} progressText="Sending..." completedText="Sent" disabled={sendDisabled()} />
                <TextInput className='new-campaign-subject' value={subject} onChange={(e: any) => setSubject(e.target.value)} disabled={isSending || hasSent} placeholder="Email Subject" />
                {hasSent && combinedTicker && <a href={Roles.HasAdminRole(user)
                    ? `/stocks/${combinedTicker}/Email Marketing`
                    : `/client-stocks/${combinedTicker}/Email Marketing/Campaigns`}>Back to Campaigns</a>}
            </div>

            <FormTextItem className='new-campaign-subject' label="Test Email Addresses" helperText="Send Preview sends to these addresses" value={testEmailAddresses} onChange={(e: any) => setTestEmailAddresses(e.target.value)} disabled={isSending || hasSent} />

            <div className='tabs'>
                <ol className='tab-list'>
                    <Tab activeTab={activeTab} label='Editor' onClick={setActiveTab} />
                    <Tab activeTab={activeTab} label='Preview' onClick={setActiveTab} />
                </ol>
                <div className='tab-content'>
                {/* keep the editor mounted when Preview is active so TinyMCE doesn't re-initialise on every tab switch */}
                <div style={{ display: activeTab === 'Editor' ? undefined : 'none' }}>
                    {!editorReady && <p>Loading editor...</p>}
                    <Editor
                        onInit={() => setEditorReady(true)}
                        disabled={isSending || hasSent}
                        apiKey="fntw2t4q38u7usw5dk4ew34dbgz96zhpezwidg8edbp5jv1k"
                        value={editorContent}
                        onEditorChange={setEditorContent}
                        init={{
                            onboarding: false,
                            height: 800,
                            menubar: false,
                            visual: false,
                            plugins: [
                                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                'insertdatetime', 'media', 'table', 'help', 'wordcount'
                            ],
                            setup: function (editor: any) {
                                editor.ui.registry.addButton('mybutton', {
                                    text: 'button',
                                    onAction: function () {
                                        editor.insertContent('<a href="#" style="display: inline-block; padding: 12px 24px; background-color: #3b82f6; color: white; text-decoration: none; border-radius: 6px; font-weight: 500;">Click Me</a>');
                                    }
                                });
                            },
                            toolbar: 'undo redo | blocks | mybutton | bold italic underline code | ' +
                                'forecolor backcolor | alignleft aligncenter alignright alignjustify | ' +
                                'bullist numlist outdent indent | removeformat | ' +
                                'table tabledelete | tableprops tablerowprops tablecellprops | ' +
                                'tableinsertrowbefore tableinsertrowafter tabledeleterow | ' +
                                'tableinsertcolbefore tableinsertcolafter tabledeletecol | ' +
                                'link image media |  fullscreen',
                            toolbar_mode: 'sliding',
                            content_style: templateCss || 'body { font-family:Arial,sans-serif; font-size:14px }',
                            valid_elements: '*[*]',
                            extended_valid_elements: '*[*]',
                            valid_children: '+body[style],+div[style]',
                            verify_html: false,
                            cleanup: false,
                            convert_urls: false,
                            remove_trailing_brs: false,
                            entity_encoding: 'raw',
                            paste_preprocess: function (plugin: any, args: any) {
                                const content = args.content;
                                const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
                                const match = content.match(youtubeRegex);

                                if (match) {
                                    const videoId = match[1];
                                    const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/sddefault.jpg`;
                                    const videoUrl = `https://www.youtube.com/watch?v=${videoId}`;

                                    args.content = `<a href="${videoUrl}" target="_blank"><img src="${thumbnailUrl}" alt="YouTube Video" style="max-width: 480px;" /></a>`;
                                }
                            }
                        }}
                    />
                </div>

                {activeTab === 'Preview' && <iframe
                    srcDoc={fullHtml()}
                    style={{
                        width: '100%',
                        height: '800px',
                        border: '1px solid #ccc'
                    }}
                    title="HTML Preview"
                    sandbox="allow-same-origin"
                />}
                </div>
            </div>
        </>}
    </Style>;
};

export default EditAnnouncementCampaignPage;
